import React from 'react'
import { useNavigate } from 'react-router'

import { createColumnHelper } from '@tanstack/react-table'
import { format, parseISO } from 'date-fns'
import styled from 'styled-components/native'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { EpisodeSummary } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { InfoIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/InfoIcon'
import { TextButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/textButton/TextButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { Tooltip } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tooltip/Tooltip'
import { DataTable } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/dataTable/DataTable'
import { colors } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/colors'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { episodeTerminationStatuses, episodeTerminationStatusesEngageDA } from '../../common/constants/appConstants'
import { CLIENT_PROGRESS } from '../../common/constants/routingConstants'
import { useAppDispatch } from '../../data/store'
import { setSelectedClientId } from '../clients/clientDetails/data/ltClientSlice'
import { useAllClientsData } from '../clients/data/hooks/useAllClientsData'

const HeaderContainer = styled.View({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
})

const ChartContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  margin: `${theme.spacing['16px']} ${theme.spacing['12px']} 0`,
}))

const CompensationCellContainer = styled.View({
  textAlign: 'center',
  flexDirection: 'row',
})

const CompensationCellTooltip = styled(Tooltip)({
  marginLeft: '4px',
})

const ClosedEpisodesCountDetails: React.FC<ClosedEpisodesCountDetailsProps> = ({ closedEpisodes }) => {
  const [clients] = useAllClientsData()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { isDAProviderMigrated } = useFlags()
  const sortedClosedEpisodes = closedEpisodes
    ? Object.values(closedEpisodes).sort((episodeA, episodeB) => {
        return episodeA.episode_end_date > episodeB.episode_end_date ? -1 : 1
      })
    : []

  const onClientClickHandler = (client_id: string) => {
    const selectedClient = clients.find(({ id }) => client_id === id)
    if (!selectedClient) {
      return
    }
    dispatch(setSelectedClientId(selectedClient.id))
    navigate(CLIENT_PROGRESS.route)
  }

  const columnHelper = createColumnHelper<episodeDetails>()

  const columns = [
    columnHelper.accessor('episode_end_date', {
      header: 'Episode Close Date',
    }),
    columnHelper.accessor('client_name', {
      header: 'Client Name',
      cell: ({ row }) => (
        <TextButton
          text={row.original.client_name}
          onPress={() => onClientClickHandler(row.original.client_id)}
          data-testid={`ClosedEpisodesClientDetails-clientName-${row.original.client_name}`}
        />
      ),
    }),
    columnHelper.accessor('baseline', {
      header: 'Baseline Severity',
    }),
    columnHelper.accessor('episode_state', {
      header: 'Episode Status',
    }),
    columnHelper.accessor('care_compensation', {
      header: 'Measurement-Base Care Compensation',
      cell: ({ row }) => (
        <CompensationCellContainer
          testID={tID(`ClosedEpisodesClientDetails-careCompensation-${row.original.care_compensation}`)}
        >
          <BodyText text={row.original.care_compensation} size={BodyTextSize.DEFAULT} color={colors.charcoal6} />
          {row.original.care_compensation === 'N/A' && (
            <CompensationCellTooltip
              content="This client did not fill out 2 or more assessments and therefore can't be evaluated for clinical success."
              testID={tID(`ClosedEpisodesClientDetails-careCompensation-tooltip`)}
              hoverEnabled
            >
              <InfoIcon size={18} />
            </CompensationCellTooltip>
          )}
        </CompensationCellContainer>
      ),
    }),
  ]

  const data = Object.values(sortedClosedEpisodes).map((episode) => {
    const terminationStatuses = isDAProviderMigrated ? episodeTerminationStatusesEngageDA : episodeTerminationStatuses
    return {
      episode_end_date: format(parseISO(episode.episode_end_date), 'MM/dd/yyyy'),
      client_name: episode.first_name + ' ' + episode.last_name,
      baseline: episode.baseline,
      episode_state: terminationStatuses.find((status) => status.value === episode.episode_state)?.name ?? '',
      care_compensation: episode.clinical_success ? 'Yes' : episode.clinical_success === false ? 'No' : 'N/A',
      client_id: episode.client_id,
    }
  })

  return (
    <div>
      <HeaderContainer>
        <Title text={'Closed Episode Client Details'} size={TitleSize.MEDIUM} color={colors.ui_oatmeal6} />
      </HeaderContainer>
      <ChartContainer testID={tID('ClosedEpisodesClientDetails-dataTable')}>
        <DataTable
          columns={columns}
          data={data}
          pagination={{ pageSize: 15 }}
          emptyTableText='You do not have any closed episodes for this quarter'
          rowHeight={48}
        />
      </ChartContainer>
    </div>
  )
}

type episodeDetails = {
  episode_end_date: string
  client_name: string
  baseline: string
  episode_state: string
  care_compensation: string
  client_id: string
}

type ClosedEpisodesCountDetailsProps = {
  closedEpisodes?: {
    [key: string]: EpisodeSummary
  }
}

export default ClosedEpisodesCountDetails
